import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigurationService } from '@app/core/config/configuration.service';
import { Settings, SettingsCmd } from '@app/core/config/settings';
import { MeldungStorno } from '@app/shared/classes/meldung-storno';
import { forkJoin, Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { Meldung, MeldungCmd } from '@app/shared/classes/meldung';
import { ImportGrund, ImportGrundAdmin, ImportGrundAdminCmd } from '@app/shared/classes/import-grund';
import { Land, LandAdmin, LandAdminCmd } from '@app/shared/classes/land';
import { MeldungFilter } from '@app/shared/classes/meldung-filter';
import { MeldungList } from '@app/shared/classes/meldung-list';
import { Status } from "@app/shared/classes/status";
import { DokumentTyp } from "@app/shared/classes/dokument-typ";
import { Dokument } from "@app/shared/classes/dokument";
import { TranslateService } from "@ngx-translate/core";
import { SettingsService } from "@app/core/config/settings.service";
import { Nutzung } from "@app/shared/classes/nutzung";
import { NotizBlvCmd, NotizKantonCmd } from "@app/shared/classes/notiz";
import { DeaktivierungsGrund, DeaktivierungsGrundAdmin, DeaktivierungsGrundAdminCmd } from '@app/shared/classes/deaktivierungs-grund';
import { TemplateType } from '@app/shared/classes/template-type';
import { MeldungBewilligung, MeldungBewilligungPreview } from '@app/shared/classes/meldung-bewilligung';
import { TemplateList } from '@app/shared/classes/template-list';
import { type Sprache } from '@app/shared/sprache';
import { HistoryEntry } from '@app/shared/classes/history-entry';

@Injectable({providedIn: 'root'})
export class BackendService {
  private readonly http = inject(HttpClient);
  private readonly configurationService = inject(ConfigurationService);
  private readonly translateService = inject(TranslateService);
  private readonly settingsService = inject(SettingsService);

  private readonly apiBase = this.configurationService.getConfig().backendUrl;

  getLog(days: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBase}/log/${days}`, {observe: 'response', responseType: 'blob'})
      .pipe();
  }

  getVersion(): Observable<any> {
    return this.http.get(`${this.apiBase}/admin/version`, {responseType: 'text'})
      .pipe();
  }

  checkBackend(): Observable<any> {
    return this.http.get(`${this.apiBase}/monitoring/checkDetails`)
      .pipe();
  }

  listSettings(): Observable<Settings[]> {
    return this.http.get<Settings[]>(`${this.apiBase}/settings`);
  }

  createSetting(setting: SettingsCmd): Observable<Settings[]> {
    return this.http.put<Settings[]>(`${this.apiBase}/settings/create`, setting);
  }

  editSetting(setting: SettingsCmd): Observable<Settings> {
    return this.http.put<Settings>(`${this.apiBase}/settings/edit`, setting);
  }

  deleteSetting(settingId: number): Observable<Settings[]> {
    return this.http.put<Settings[]>(`${this.apiBase}/settings/delete`, settingId);
  }

  listZulassungsLaender(): Observable<Land[]> {
    return this.http.get<Land[]>(`${this.apiBase}/settings/zulassungsLaender`);
  }

  listImportLaender(): Observable<Land[]> {
    return this.http.get<Land[]>(`${this.apiBase}/settings/importLaender`);
  }

  listVerwendeteLaender(): Observable<Land[]> {
    return this.http.get<Land[]>(`${this.apiBase}/settings/laender/search`);
  }

  listLaender(): Observable<LandAdmin[]> {
    return this.http.get<LandAdmin[]>(`${this.apiBase}/settings/laender/admin`);
  }

  listStatus(): Observable<Status[]> {
    return this.http.get<Status[]>(`${this.apiBase}/settings/status`);
  }

  listNutzung(): Observable<Nutzung[]> {
    return this.http.get<Nutzung[]>(`${this.apiBase}/settings/nutzung`);
  }

  createLand(land: LandAdminCmd): Observable<LandAdmin[]> {
    return this.http.put<LandAdmin[]>(`${this.apiBase}/settings/laender/create`, land);
  }

  editLand(land: LandAdminCmd): Observable<LandAdmin> {
    return this.http.put<LandAdmin>(`${this.apiBase}/settings/laender/edit`, land);
  }

  deleteLand(landId: number): Observable<LandAdmin[]> {
    return this.http.put<LandAdmin[]>(`${this.apiBase}/settings/laender/delete`, landId);
  }

  listImportGruende(): Observable<ImportGrund[]> {
    return this.http.get<ImportGrund[]>(`${this.apiBase}/settings/importGruende`);
  }

  listImportGruendeAdmin(): Observable<ImportGrundAdmin[]> {
    return this.http.get<ImportGrundAdmin[]>(`${this.apiBase}/settings/importGruende/admin`);
  }

  createImportgrund(grund: ImportGrundAdminCmd): Observable<ImportGrundAdmin[]> {
    return this.http.put<ImportGrundAdmin[]>(`${this.apiBase}/settings/importGruende/create`, grund);
  }

  editImportgrund(grund: ImportGrundAdminCmd): Observable<ImportGrundAdmin> {
    return this.http.put<ImportGrundAdmin>(`${this.apiBase}/settings/importGruende/edit`, grund);
  }

  deleteImportgrund(grundId: number): Observable<ImportGrundAdmin[]> {
    return this.http.put<ImportGrundAdmin[]>(`${this.apiBase}/settings/importGruende/delete`, grundId);
  }

  listDeaktivierungsGruende(): Observable<DeaktivierungsGrund[]> {
    return this.http.get<DeaktivierungsGrund[]>(`${this.apiBase}/settings/deaktivierungsGruende`);
  }

  listDeaktivierungsGruendeAdmin(): Observable<DeaktivierungsGrundAdmin[]> {
    return this.http.get<DeaktivierungsGrundAdmin[]>(`${this.apiBase}/settings/deaktivierungsGruende/admin`);
  }

  createDeaktivierungsGrund(grund: DeaktivierungsGrundAdminCmd): Observable<DeaktivierungsGrundAdmin[]> {
    return this.http.put<DeaktivierungsGrundAdmin[]>(`${this.apiBase}/settings/deaktivierungsGruende/create`, grund);
  }

  editDeaktivierungsGrund(grund: DeaktivierungsGrundAdminCmd): Observable<DeaktivierungsGrundAdmin> {
    return this.http.put<DeaktivierungsGrundAdmin>(`${this.apiBase}/settings/deaktivierungsGruende/edit`, grund)
  }

  deleteDeaktivierungsGrund(grundId: number): Observable<DeaktivierungsGrundAdmin[]> {
    return this.http.put<DeaktivierungsGrundAdmin[]>(`${this.apiBase}/settings/deaktivierungsGruende/delete`, grundId);
  }

  listTemplateTypes(): Observable<TemplateList> {
    return this.http.get<TemplateList>(`${this.apiBase}/settings/template`);
  }

  uploadTemplate(type: TemplateType, sprache: Sprache, file: File): Observable<any> {
    let maxSize = Number.parseFloat(this.settingsService.get("dokument.maxSize") ?? '') || 10;
    if (file.size > maxSize * 1024 * 1024) {
      return throwError(() => this.translateService.instant('dokument.maxSize', {size: maxSize}));
    }
    const formData = new FormData();
    formData.append('name', file.name);
    formData.append('data', file);
    return this.http.post(`${this.apiBase}/settings/template/${type.id}/${sprache}`, formData)
      .pipe();
  }

  downloadTemplate(type: TemplateType, sprache: Sprache): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBase}/settings/template/${type.id}/${sprache}`, {observe: 'response', responseType: 'blob'});
  }

  downloadDummyTemplate(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBase}/settings/template/dummy`, {observe: 'response', responseType: 'blob'});
  }

  createMeldung(meldung: MeldungCmd): Observable<number> {
    return this.http.post(`${this.apiBase}/meldung/create`, meldung)
      .pipe(
        map((data: any) => data['id'])
      );
  }

  getMeldung(id: string|number): Observable<Meldung> {
    return this.http.get<Meldung>(`${this.apiBase}/meldung/${id}`);
  }

  listMeldungen(filter: MeldungFilter): Observable<MeldungList> {
    return this.http.post<MeldungList>(`${this.apiBase}/meldung/list`, filter);
  }

  stornoMeldung(storno: MeldungStorno): Observable<Meldung> {
    return this.http.put<Meldung>(`${this.apiBase}/meldung/storno`, storno);
  }

  bewilligeMeldungPreview(bewilligung: MeldungBewilligung): Observable<MeldungBewilligungPreview> {
    return this.http.put<MeldungBewilligungPreview>(`${this.apiBase}/meldung/bewillige/preview`, bewilligung);
  }

  bewilligeMeldung(bewilligung: MeldungBewilligung): Observable<Meldung> {
    return this.http.put<Meldung>(`${this.apiBase}/meldung/bewillige`, bewilligung);
  }

  letzteSprache(id: string|number): Observable<Sprache | undefined> {
    return this.http.get(`${this.apiBase}/meldung/sprache/${id}`, {responseType: 'text'})
      .pipe(
        map((data: string) => {
          if (data === 'DE' || data === 'FR'|| data === 'IT') {
            return data;
          }
          return undefined;
        })
      );
  }

  saveNotizBlv(notiz: NotizBlvCmd): Observable<Meldung> {
    return this.http.put<Meldung>(`${this.apiBase}/meldung/notiz/blv`, notiz);
  }

  saveNotizKanton(notiz: NotizKantonCmd): Observable<Meldung> {
    return this.http.put<Meldung>(`${this.apiBase}/meldung/notiz/kanton`, notiz);
  }

  getMeldungPdf(id: number|string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBase}/meldung/pdf/${id}`, {observe: 'response', responseType: 'blob'});
  }

  getMeldungenCsv(filter: MeldungFilter): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.apiBase}/meldung/csv`, filter, {observe: 'response', responseType: 'blob'});
  }

  getMeldungenXls(filter: MeldungFilter): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.apiBase}/meldung/xls`, filter, {observe: 'response', responseType: 'blob'});
  }

  getDatenauszugZoll(filter: MeldungFilter): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.apiBase}/meldung/bazg`, filter, {observe: 'response', responseType: 'blob'});
  }

  listDokumente(id: number|string, kopieren?: boolean): Observable<Dokument[]> {
    let suffix = kopieren ? '?copy' : '';
    return this.http.get<Dokument[]>(`${this.apiBase}/dokument/list/${id}${suffix}`);
  }

  listAvailableUploads(id: number|string): Observable<DokumentTyp[]> {
    return this.http.get<DokumentTyp[]>(`${this.apiBase}/dokument/availableUploads/${id}`);
  }

  uploadDokument(dokumentTyp: DokumentTyp, file: File): Observable<Dokument> {
    let maxSize = Number.parseFloat(this.settingsService.get("dokument.maxSize") ?? '') || 10;
    if (file.size > maxSize * 1024 * 1024) {
      return throwError(() => this.translateService.instant('dokument.maxSize', {size: maxSize}));
    }
    const formData = new FormData();
    formData.append('dokumentTyp', dokumentTyp.id.toString());
    formData.append('name', file.name);
    formData.append('data', file);
    return this.http.post<Dokument>(`${this.apiBase}/dokument/upload`, formData);
  }

  linkDokumente(meldungId: number, dokumentIds: number[]): Observable<Dokument[]> {
    return this.http.put<Dokument[]>(`${this.apiBase}/dokument/link`, { meldungId, dokumentIds } );
  }

  getDokumente(ids: number[]): Observable<Dokument[]> {
    return forkJoin(
      ids.map( it => this.http.get<Dokument>(`${this.apiBase}/dokument/info/${it}`) )
    );
  }

  downloadDokument(dokument: Dokument): Observable<HttpResponse<Blob>> {
    if (dokument.temporary) {
      return this.http.get(`${this.apiBase}/dokument/temp/${dokument.id}`, {observe: 'response', responseType: 'blob'});
    }
    return this.http.get(`${this.apiBase}/dokument/${dokument.id}`, {observe: 'response', responseType: 'blob'});
  }

  downloadBewilligung(id: number|string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBase}/dokument/bewilligung/${id}`, {observe: 'response', responseType: 'blob'});
  }

  markUnread(dokument: Dokument) {
    return this.http.get(`${this.apiBase}/dokument/unread/${dokument.id}`);
  }

  deleteDokument(dokument: Dokument) {
    let suffix = dokument.temporary ? '?temp' : ''
    return this.http.delete(`${this.apiBase}/dokument/delete/${dokument.id}${suffix}`);
  }

  getDokumentTypen(ids: number[]): Observable<DokumentTyp[]> {
    return forkJoin(
      ids.map( it => this.http.get<DokumentTyp>(`${this.apiBase}/dokument/typ/${it}`) )
    );
  }

  listDokumentTypen(): Observable<DokumentTyp[]> {
    return this.http.get<DokumentTyp[]>(`${this.apiBase}/dokument/typ`);
  }

  listHistoryEntries(meldung: Meldung): Observable<HistoryEntry[]> {
    return this.http.get<HistoryEntry[]>(`${this.apiBase}/history/${meldung.id}`);
  }

  getI18nExcel(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBase}/admin/i18n`, {observe: 'response', responseType: 'blob'});
  }

  convertI18nExcel(file: File): Observable<HttpResponse<Blob>> {
    const formData = new FormData();
    formData.append('name', file.name);
    formData.append('data', file);
    return this.http.post(`${this.apiBase}/admin/i18n`, formData, {observe: 'response', responseType: 'blob'});
  }
}
